/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { AmiReading } from '../../api/ami';
import type { Reading, SeriesPoint } from './types';

export const NON_NUMERIC_CODES = new Set(['0.0.0_0', '0.9.1_0', '0.9.2_0']);

const DECIMAL = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/;

export function normaliseReadings(raw: AmiReading[]): Reading[] {
  return raw
    .filter((reading) => typeof reading?.serial === 'string' && reading.serial !== ''
      && typeof reading.timestamp === 'number' && Number.isFinite(reading.timestamp))
    .map((reading) => ({
      serial: (reading.serial as string).toUpperCase(),
      timestamp: reading.timestamp as number,
      operationCode: reading.operationCode,
      partial: reading.hasPartialResult === true,
      values: textValues(reading.response),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

function textValues(response: AmiReading['response']): Record<string, string> {
  const values: Record<string, string> = {};
  if (response && typeof response === 'object') {
    for (const [code, value] of Object.entries(response)) {
      if (value !== null && value !== undefined) {
        values[code] = String(value);
      }
    }
  }
  return values;
}

export function numericValue(value: string | undefined): number | null {
  if (value === undefined) {
    return null;
  }
  const text = value.trim();
  if (!DECIMAL.test(text)) {
    return null;
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

export function seriesFor(readings: Reading[], code: string): SeriesPoint[] {
  return readings.map((reading) => ({ t: reading.timestamp, v: numericValue(reading.values[code]) }));
}

export function mergeReadings(first: Reading[], second: Reading[]): Reading[] {
  const seen = new Set(first.map(readingKey));
  const merged = [...first, ...second.filter((reading) => !seen.has(readingKey(reading)))];
  return merged.sort((a, b) => a.timestamp - b.timestamp);
}

function readingKey(reading: Reading): string {
  return `${reading.serial}|${reading.timestamp}|${reading.operationCode ?? ''}`;
}

export function chartableCodes(readings: Reading[]): Set<string> {
  const codes = new Set<string>();
  for (const reading of readings) {
    for (const [code, value] of Object.entries(reading.values)) {
      if (!codes.has(code) && numericValue(value) !== null) codes.add(code);
    }
  }
  return codes;
}

export function commonCodes(lists: ReadonlySet<string>[]): Set<string> | null {
  const nonEmpty = lists.filter((codes) => codes.size > 0);
  if (nonEmpty.length === 0) return null;
  return nonEmpty.slice(1).reduce<Set<string>>(
    (common, next) => new Set([...common].filter((code) => next.has(code))),
    new Set(nonEmpty[0]),
  );
}

export function withReadingCodes(codes: ReadonlySet<string>, reading: Reading): ReadonlySet<string> {
  const added = Object.entries(reading.values)
    .filter(([code, value]) => !codes.has(code) && numericValue(value) !== null)
    .map(([code]) => code);
  return added.length === 0 ? codes : new Set([...codes, ...added]);
}
